import React, { createContext, useContext, useState, useEffect } from 'react';

type TextSize = 'small' | 'medium' | 'large' | 'extra-large';

interface AccessibilityContextType {
  textSize: TextSize;
  highContrast: boolean;
  setTextSize: (size: TextSize) => void;
  toggleHighContrast: () => void;
  resetSettings: () => void;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const AccessibilityProvider = ({ children }: { children: React.ReactNode }) => {
  const [textSize, setTextSize] = useState<TextSize>('medium');
  const [highContrast, setHighContrast] = useState(false);

  useEffect(() => {
    // Apply text size class to root element
    const root = document.documentElement;
    root.classList.remove('text-small', 'text-medium', 'text-large', 'text-extra-large');
    root.classList.add(`text-${textSize}`);
  }, [textSize]);

  useEffect(() => {
    // Apply high contrast class to root element
    const root = document.documentElement;
    if (highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
  }, [highContrast]);

  const toggleHighContrast = () => {
    setHighContrast(!highContrast);
  };

  const resetSettings = () => {
    setTextSize('medium');
    setHighContrast(false);
  };

  return (
    <AccessibilityContext.Provider
      value={{
        textSize,
        highContrast,
        setTextSize,
        toggleHighContrast,
        resetSettings,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};
